'use client';

/**
 * TourTriggerMount — orchestratorul invizibil pentru toate tururile ghidate.
 *
 * Multi-tour: lucrează cu activeTourId din Zustand store. Folosește registry-ul
 * de tururi pentru a obține pașii pe baza ID-ului activ.
 *
 * Responsabilități:
 *  1. Auto-detectează utilizatori noi pentru tour-ul 'overview' (pe ruta `/`).
 *  2. Randează WelcomeModal pentru tour-ul 'overview' (când phase==='welcome').
 *  3. Pentru orice tur active: orchestrare Driver.js spotlight + rerandare TourTooltip.
 *  4. Pe complete/skip apelează API-ul pentru tour-ul activ.
 */

import { useEffect, useMemo, useRef } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { toast } from 'sonner';
import { createElement } from 'react';
import {
  LayoutDashboard,
  FileText,
  ClipboardCheck,
  FileKey2,
  Wallet,
  Upload,
  Banknote,
} from 'lucide-react';

// Per-tour icon (reflectă icon-ul paginii din sidebar)
const TOUR_ICONS: Record<string, typeof LayoutDashboard> = {
  'overview': LayoutDashboard,
  'first-invoice': FileText,
  'nir-reception': ClipboardCheck,
  'efactura-setup': FileKey2,
  'expense-new': Wallet,
  'data-import': Upload,
  'payment-receive': Banknote,
};
import { createRoot, type Root } from 'react-dom/client';
import { driver, type Driver, type DriveStep } from 'driver.js';
import 'driver.js/dist/driver.css';
import { useAuthStore } from '@/features/auth/stores/auth-store';
import { useTour, useActiveTour } from '@/features/tour/hooks/use-product-tour';
import { getTour, getEffectiveTourSteps } from '@/features/tour/lib/tour-registry';
import { useSidebarGroupsStore } from '@/shared/stores/sidebar-groups-store';
import { WelcomeModal } from '@/features/tour/components/welcome-modal';
import { TourTooltip } from '@/features/tour/components/tour-tooltip';

const AUTO_OPEN_DELAY_MS = 1200;

function firstName(name: string | undefined): string | undefined {
  if (!name) return undefined;
  const trimmed = name.trim();
  if (!trimmed) return undefined;
  return trimmed.split(/\s+/)[0];
}

export function TourTriggerMount() {
  const pathname = usePathname();
  const user = useAuthStore((s) => s.user);

  // State live din Zustand pentru tour-ul activ (orice ID)
  const { activeTourId, phase, stepIndex } = useActiveTour();

  // Hook pentru overview (folosit DOAR pentru auto-trigger logic + WelcomeModal pe overview)
  const overview = useTour('overview');
  const { start: startOverview, skip: skipOverview, openWelcome: openOverviewWelcome } = overview;

  // Acțiuni live pe tour-ul activ (oricare ar fi)
  const tourLive = useTour(activeTourId ?? 'overview');
  const { next, prev, skip: skipActive, complete: completeActive } = tourLive;

  // ── 1. Auto-trigger 'overview' pentru useri noi pe `/` ─────────────────
  const autoOpenedRef = useRef(false);
  useEffect(() => {
    if (autoOpenedRef.current) return;
    if (!user) return;
    if (!overview.shouldAutoStart) return;
    if (pathname !== '/') return;
    if (phase !== 'idle') return;
    autoOpenedRef.current = true;
    const t = window.setTimeout(() => {
      openOverviewWelcome();
    }, AUTO_OPEN_DELAY_MS);
    return () => window.clearTimeout(t);
  }, [user, overview.shouldAutoStart, pathname, phase, openOverviewWelcome]);

  // ── 2. Driver.js orchestrare pentru tour-ul activ ──────────────────────
  const driverRef = useRef<Driver | null>(null);
  const rootRef = useRef<Root | null>(null);
  const stepIndexRef = useRef(stepIndex);
  stepIndexRef.current = stepIndex;
  const activeTourIdRef = useRef(activeTourId);
  activeTourIdRef.current = activeTourId;

  // Pașii EFECTIVI pentru tour-ul activ — exclude pașii skipIfHidden cu țintă
  // invizibilă (ex. Setup checklist ascuns la 100% configurare). Aceeași filtrare
  // e folosită și de store (next/goTo) și useTour → indexuri consistente.
  const activeTourSteps = useMemo(() => {
    if (!activeTourId) return [];
    return getEffectiveTourSteps(activeTourId);
  }, [activeTourId]);

  // ── Completion celebration: toast + next-step CTA ──────────────
  const router = useRouter();
  const prevPhaseRef = useRef(phase);
  useEffect(() => {
    const prev = prevPhaseRef.current;
    prevPhaseRef.current = phase;
    if (prev === 'running' && phase === 'finished' && activeTourId) {
      const tour = getTour(activeTourId);
      const c = tour?.completion;
      if (c) {
        const Icon = TOUR_ICONS[activeTourId];
        toast.success(c.message, {
          duration: 8000,
          icon: Icon ? createElement(Icon, { className: 'h-4 w-4 text-emerald-600' }) : undefined,
          action: c.cta
            ? {
                label: c.cta.label,
                onClick: () => {
                  if (c.cta?.href) router.push(c.cta.href);
                },
              }
            : undefined,
        });
      }
      // Auto-close phase after short delay (lets toast appear cleanly)
      const closeFn = tourLive.close;
      window.setTimeout(() => {
        closeFn();
      }, 200);
    }
  }, [phase, activeTourId, router]);

  function alignFor(s: (typeof activeTourSteps)[number]): 'start' | 'center' | 'end' {
    // 1. Explicit override din registry — întotdeauna câștigă
    if (s.align) return s.align;
    // 2. Top-bar items (placement left/right, fără sidebarGroup) — align='start' ca să nu iasă sus.
    if (!s.sidebarGroup && (s.placement === 'left' || s.placement === 'right')) return 'start';
    // 3. Tot restul (sidebar items, full-width sections) — center, garantat în viewport.
    return 'center';
  }

  /**
   * Auto-collision detection: alege automat partea cu cel mai mult spațiu liber în viewport.
   * Preferă placement-ul configurat dacă încape; altfel flip la opus, apoi perpendicular,
   * altfel partea cu cel mai mult spațiu. Garantează că tooltip-ul NU iese din viewport ȘI
   * NU se suprapune peste elementul țintă.
   */
  function pickBestSide(
    el: HTMLElement,
    preferred: 'top' | 'bottom' | 'left' | 'right',
  ): 'top' | 'bottom' | 'left' | 'right' {
    if (typeof window === 'undefined') return preferred;
    const rect = el.getBoundingClientRect();
    // Includem popoverOffset (16) + arrow size (~7) + padding interior tooltip (~20)
    const popoverW = 380;
    const popoverH = 240;
    const margin = 32; // mai conservator: dacă spațiul e marginal, flip la altă parte
    const viewportW = window.innerWidth;
    const viewportH = window.innerHeight;

    const spaces = {
      right: viewportW - rect.right - margin,
      left: rect.left - margin,
      bottom: viewportH - rect.bottom - margin,
      top: rect.top - margin,
    };
    const fits = {
      right: spaces.right >= popoverW,
      left: spaces.left >= popoverW,
      bottom: spaces.bottom >= popoverH,
      top: spaces.top >= popoverH,
    };

    // 1. Preferred fits → keep it
    if (fits[preferred]) return preferred;

    // 2. Try opposite (flip)
    const opposite: Record<string, 'top' | 'bottom' | 'left' | 'right'> = {
      right: 'left', left: 'right', top: 'bottom', bottom: 'top',
    };
    if (fits[opposite[preferred]]) return opposite[preferred];

    // 3. Try perpendicular axis
    const perpendicular: ('top' | 'bottom' | 'left' | 'right')[] =
      preferred === 'left' || preferred === 'right' ? ['bottom', 'top'] : ['right', 'left'];
    for (const side of perpendicular) {
      if (fits[side]) return side;
    }

    // 4. Fallback: side with most space
    const sorted = (Object.entries(spaces) as ['top' | 'bottom' | 'left' | 'right', number][])
      .sort((a, b) => b[1] - a[1]);
    return sorted[0][0];
  }

  /**
   * Construiește lista de pași Driver.js cu side-ul recalculat doar pentru pasul activ
   * pe baza dimensiunilor reale ale elementului țintă (auto-collision detection).
   * Restul pașilor folosesc placement-ul configurat (Driver.js îi rezolvă la moveTo time).
   */
  function buildStepsForVisibleTarget(idx: number, el: HTMLElement): DriveStep[] {
    return activeTourSteps.map((s, i) => {
      if (i !== idx) {
        return {
          element: s.selector,
          popover: {
            description: '',
            side: s.placement ?? 'right',
            align: alignFor(s),
          },
        };
      }
      const preferred = (s.placement ?? 'right') as 'top' | 'bottom' | 'left' | 'right';
      const bestSide = pickBestSide(el, preferred);
      // Compute align based on the effective side (placement may have changed)
      const effective = { ...s, placement: bestSide };
      return {
        element: s.selector,
        popover: {
          description: '',
          side: bestSide,
          align: alignFor(effective),
        },
      };
    });
  }

  const driveSteps: DriveStep[] = useMemo(
    () =>
      activeTourSteps.map((s) => ({
        element: s.selector,
        popover: {
          description: '',
          side: s.placement ?? 'right',
          align: alignFor(s),
        },
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeTourSteps],
  );

  function findVisibleTarget(selector: string): HTMLElement | null {
    const all = document.querySelectorAll<HTMLElement>(selector);
    for (const el of Array.from(all)) {
      let cur: HTMLElement | null = el;
      let hidden = false;
      while (cur && cur !== document.body) {
        // `display: contents` nu generează box → offsetHeight/Width sunt MEREU 0, dar
        // elementul NU își ascunde copiii. Fără excepția asta, un wrapper display:contents
        // din lanțul de părinți marca ținta ca „ascunsă" la fiecare frame → waitForVisibleTarget
        // atingea timeout-ul de 2500ms la FIECARE pas (primul pas apărea în ~4s). Confirmat live.
        if (getComputedStyle(cur).display !== 'contents' && (cur.offsetHeight === 0 || cur.offsetWidth === 0)) {
          hidden = true;
          break;
        }
        cur = cur.parentElement;
      }
      if (hidden) continue;
      const rect = el.getBoundingClientRect();
      if (rect.width >= 5 && rect.height >= 5 && rect.left >= 0) {
        return el;
      }
    }
    return null;
  }

  function waitForVisibleTarget(stepIdx: number): Promise<HTMLElement | null> {
    return new Promise((resolve) => {
      const step = activeTourSteps[stepIdx];
      if (!step) return resolve(null);
      // Pinează grupul-țintă în store (override peste auto-colaps, supraviețuiește
      // re-mount-ului sidebar-ului). Pentru pași fără grup (ex. top-bar), dez-pinează.
      useSidebarGroupsStore.getState().pinGroup(step.sidebarGroup ?? null);
      const start = performance.now();
      // 2500ms acoperă deschiderea + animația grupului din sidebar (pașii cu sidebarGroup).
      // Pentru pași FĂRĂ sidebarGroup nu există nimic de deschis/așteptat: ținta ori e deja
      // vizibilă, ori nu va deveni (ex. butonul Setup checklist se auto-ascunde la 100%
      // configurare → wrapper-ul data-tour rămâne 0×0). Fără scurtătura asta, tranziția la
      // un asemenea pas îngheța ~2.5s pe conturile complet configurate.
      const timeout = step.sidebarGroup ? 2500 : 400;
      function poll() {
        const el = findVisibleTarget(step.selector);
        if (el) {
          try {
            el.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'auto' });
          } catch { /* ignore */ }
          window.setTimeout(() => resolve(el), 60);
          return;
        }
        if (performance.now() - start > timeout) return resolve(null);
        window.requestAnimationFrame(poll);
      }
      poll();
    });
  }

  // Pornește/oprește Driver.js când phase devine 'running' / iese din 'running'
  useEffect(() => {
    if (phase !== 'running' || !activeTourId || activeTourSteps.length === 0) return;

    const d = driver({
      showProgress: false,
      showButtons: [],
      allowClose: false,
      overlayColor: 'rgba(15, 23, 42, 0.5)',
      overlayOpacity: 0.5,
      stagePadding: 8,
      stageRadius: 12,
      popoverOffset: 16,
      smoothScroll: true,
      disableActiveInteraction: true,
      steps: driveSteps,
      onPopoverRender: (popover) => {
        const wrapper = popover.wrapper;
        wrapper.classList.add('sf-tour-driver-popover');
        if (popover.title) popover.title.style.display = 'none';
        if (popover.footer) popover.footer.style.display = 'none';
        const mountNode = popover.description;
        if (!mountNode) return;
        mountNode.innerHTML = '';
        if (rootRef.current) {
          const stale = rootRef.current;
          rootRef.current = null;
          queueMicrotask(() => { try { stale.unmount(); } catch { /* ignore */ } });
        }
        rootRef.current = createRoot(mountNode);
        renderTooltip(stepIndexRef.current);
        queueMicrotask(() => {
          try {
            (driverRef.current as unknown as { refresh?: () => void } | null)?.refresh?.();
          } catch { /* ignore */ }
        });
      },
    });

    driverRef.current = d;
    // Forțează sidebar-ul expanded pentru tururi care țintesc sidebar items
    if (typeof window !== 'undefined') {
      window.dispatchEvent(
        new CustomEvent('sf-sidebar:set-collapsed', { detail: { collapsed: false } }),
      );
    }
    void waitForVisibleTarget(0).then((el) => {
      try {
        // Auto-collision detect doar pentru step 0 (singurul element vizibil garantat la start).
        // Pentru pașii următori, Driver.js folosește placement-ul configurat — dacă apare overlap,
        // ajustăm placement-ul în registry pentru pasul respectiv.
        if (el) d.setSteps(buildStepsForVisibleTarget(0, el));
        d.drive(0);
      } catch { /* ignore */ }
    });

    return () => {
      // Dez-pinează grupul → sidebar-ul revine la reconcilierea normală.
      useSidebarGroupsStore.getState().pinGroup(null);
      const driverInstance = driverRef.current;
      const rootInstance = rootRef.current;
      driverRef.current = null;
      rootRef.current = null;
      queueMicrotask(() => {
        try { driverInstance?.destroy(); } catch { /* ignore */ }
        try { rootInstance?.unmount(); } catch { /* ignore */ }
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, activeTourId]);

  // Move la pasul corect când stepIndex se schimbă
  useEffect(() => {
    if (phase !== 'running' || !activeTourId) return;
    const d = driverRef.current;
    if (!d) return;
    const active = d.getActiveIndex();
    if (active !== stepIndex) {
      void waitForVisibleTarget(stepIndex).then(() => {
        try {
          d.moveTo(stepIndex);
        } catch { /* ignore */ }
        renderTooltip(stepIndex);
      });
    } else {
      renderTooltip(stepIndex);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepIndex, phase, activeTourId]);

  function renderTooltip(idx: number) {
    if (!rootRef.current) return;
    const step = activeTourSteps[idx];
    if (!step) return;
    rootRef.current.render(
      <TourTooltip
        step={{ id: step.id, title: step.title, body: step.body }}
        stepIndex={idx}
        totalSteps={activeTourSteps.length}
        onNext={next}
        onPrev={prev}
        onSkip={skipActive}
        onComplete={completeActive}
      />,
    );
  }

  // ── 3. Render WelcomeModal — DOAR pentru tour-ul 'overview' ────────────
  return (
    <>
      <WelcomeModal
        open={activeTourId === 'overview' && phase === 'welcome'}
        onStart={startOverview}
        onSkip={skipOverview}
        userName={firstName(user?.name)}
      />
      {/* Driver.js global overrides */}
      <style>{`
        .sf-tour-driver-popover {
          padding: 0 !important;
          background: transparent !important;
          border: 0 !important;
          box-shadow: none !important;
          max-width: 360px !important;
        }
        .sf-tour-driver-popover .driver-popover-title,
        .sf-tour-driver-popover .driver-popover-description,
        .sf-tour-driver-popover .driver-popover-footer,
        .sf-tour-driver-popover .driver-popover-close-btn,
        .sf-tour-driver-popover .driver-popover-progress-text {
          all: unset;
        }
        .sf-tour-driver-popover .driver-popover-description {
          display: block !important;
        }
        .sf-tour-driver-popover .driver-popover-footer,
        .sf-tour-driver-popover .driver-popover-close-btn {
          display: none !important;
        }
        .sf-tour-driver-popover .driver-popover-arrow {
          border-width: 7px !important;
        }
        .sf-tour-driver-popover .driver-popover-arrow-side-top { border-top-color: #FAF8F4 !important; }
        .sf-tour-driver-popover .driver-popover-arrow-side-bottom { border-bottom-color: #FAF8F4 !important; }
        .sf-tour-driver-popover .driver-popover-arrow-side-left { border-left-color: #FAF8F4 !important; }
        .sf-tour-driver-popover .driver-popover-arrow-side-right { border-right-color: #FAF8F4 !important; }
        :root.dark .sf-tour-driver-popover .driver-popover-arrow-side-top { border-top-color: #0a0a0a !important; }
        :root.dark .sf-tour-driver-popover .driver-popover-arrow-side-bottom { border-bottom-color: #0a0a0a !important; }
        :root.dark .sf-tour-driver-popover .driver-popover-arrow-side-left { border-left-color: #0a0a0a !important; }
        :root.dark .sf-tour-driver-popover .driver-popover-arrow-side-right { border-right-color: #0a0a0a !important; }
        .driver-active-element {
          z-index: 999999 !important;
          position: relative !important;
        }
        .driver-active-element::after {
          content: '';
          position: absolute;
          inset: -6px;
          border-radius: 12px;
          pointer-events: none;
          box-shadow:
            0 0 0 2px rgba(245, 158, 11, 0.85),
            0 0 24px 4px rgba(245, 158, 11, 0.45),
            0 0 48px 8px rgba(245, 158, 11, 0.2);
          animation: sfTourPulse 2s ease-in-out infinite;
        }
        @keyframes sfTourPulse {
          0%, 100% { box-shadow: 0 0 0 2px rgba(245, 158, 11, 0.85), 0 0 24px 4px rgba(245, 158, 11, 0.45), 0 0 48px 8px rgba(245, 158, 11, 0.2); }
          50% { box-shadow: 0 0 0 2px rgba(245, 158, 11, 1), 0 0 32px 6px rgba(245, 158, 11, 0.55), 0 0 64px 12px rgba(245, 158, 11, 0.25); }
        }
        :root:not(.dark) .driver-active-element {
          background-color: rgba(255, 251, 235, 0.95) !important;
        }
        :root.dark .driver-active-element {
          background-color: rgba(254, 243, 199, 0.12) !important;
        }
        .driver-overlay {
          backdrop-filter: blur(1px);
        }
      `}</style>
    </>
  );
}
